import React, { useState } from "react";
import {
    Typography,
    Box,
    FormControlLabel,
    Switch,
    Divider,
    Button,
    RadioGroup,
    Radio,
    FormControl,
    FormLabel,
    Alert,
    Snackbar,
    Card,
    CardContent,
} from "@mui/material";
import classes from "../Settings.module.scss";
import { ComingSoonOverlay } from "../../../components/ui/ComingSoonOverlay/ComingSoonOverlay";

const NotificationsTab: React.FC = () => {
    // Notification settings state
    const [emailNotifications, setEmailNotifications] = useState(true);
    const [pushNotifications, setPushNotifications] = useState(true);
    const [smsNotifications, setSmsNotifications] = useState(false);

    // Email frequency settings
    const [emailFrequency, setEmailFrequency] = useState("daily");

    // Notification types
    const [notificationTypes, setNotificationTypes] = useState({
        mentions: true,
        comments: true,
        messages: true,
        connections: true,
        posts: true,
        events: false,
        news: false,
        updates: true,
    });

    // Success notification state
    const [showNotification, setShowNotification] = useState(false);

    // Handle notification type toggle
    const handleNotificationTypeToggle =
        (type: keyof typeof notificationTypes) => (event: React.ChangeEvent<HTMLInputElement>) => {
            setNotificationTypes({
                ...notificationTypes,
                [type]: event.target.checked,
            });
            setShowNotification(true);
        };

    // Handle email frequency change
    const handleEmailFrequencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailFrequency(event.target.value);
        setShowNotification(true);
    };

    // Handle main toggle changes
    const handleToggleChange =
        (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setter(event.target.checked);
            setShowNotification(true);
        };

    // Close notification
    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    // Unsubscribe from all notifications
    const unsubscribeAll = () => {
        setEmailNotifications(false);
        setPushNotifications(false);
        setSmsNotifications(false);
        setNotificationTypes({
            mentions: false,
            comments: false,
            messages: false,
            connections: false,
            posts: false,
            events: false,
            news: false,
            updates: false,
        });
        setShowNotification(true);
    };

    return (
        <Box style={{ position: "relative" }}>
            <Box className={classes.contentHeader}>
                <Typography variant="h5" component="h1">
                    Notification Settings
                </Typography>
                <Typography variant="body2">
                    Manage how and when you receive notifications from MAX Aspire
                </Typography>
            </Box>

            {/* Main Notification Channels */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Notification Channels
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Smart notification system launching soon! Share your preferences."
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <Box className={classes.switchContainer}>
                                <Box>
                                    <Typography variant="body1">Email Notifications</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Receive notifications via email
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={emailNotifications}
                                            onChange={handleToggleChange(setEmailNotifications)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography variant="body1">Push Notifications</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Receive notifications on your browser or mobile device
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={pushNotifications}
                                            onChange={handleToggleChange(setPushNotifications)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography variant="body1">SMS Notifications</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Receive important notifications via text message
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={smsNotifications}
                                            onChange={handleToggleChange(setSmsNotifications)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Email Frequency Settings */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Email Frequency
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Email frequency settings coming very soon! Share your preferences."
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <FormControl component="fieldset">
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        color: "var(--text-color)",
                                        "&.Mui-focused": { color: "var(--primary-color)" },
                                    }}
                                >
                                    How often would you like to receive email digests?
                                </FormLabel>
                                <RadioGroup
                                    aria-label="email-frequency"
                                    name="email-frequency"
                                    value={emailFrequency}
                                    onChange={handleEmailFrequencyChange}
                                >
                                    <FormControlLabel
                                        value="realtime"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Real-time - Send emails as activities happen"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                    <FormControlLabel
                                        value="daily"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Daily - Send a daily digest of activities"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                    <FormControlLabel
                                        value="weekly"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Weekly - Send a weekly digest of activities"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: "var(--border-color)", opacity: 0.6 }} />

            {/* Notification Types */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Notification Types
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Notification types coming very soon! Share your preferences."
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <Typography variant="body2" color="textSecondary" mb={2}>
                                Choose which types of activities you&apos;d like to be notified
                                about
                            </Typography>

                            <Box className={classes.switchContainer}>
                                <Typography variant="body1">Someone mentions you</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.mentions}
                                            onChange={handleNotificationTypeToggle("mentions")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">
                                    Someone comments on your post
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.comments}
                                            onChange={handleNotificationTypeToggle("comments")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">Someone sends you a message</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.messages}
                                            onChange={handleNotificationTypeToggle("messages")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">
                                    Connection requests and updates
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.connections}
                                            onChange={handleNotificationTypeToggle("connections")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">
                                    New posts from your connections
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.posts}
                                            onChange={handleNotificationTypeToggle("posts")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">Events and meetups</Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.events}
                                            onChange={handleNotificationTypeToggle("events")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">
                                    MAX Aspire news and announcements
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.news}
                                            onChange={handleNotificationTypeToggle("news")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Typography variant="body1">
                                    Platform updates and features
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={notificationTypes.updates}
                                            onChange={handleNotificationTypeToggle("updates")}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Unsubscribe from all */}
            <Box mt={4} textAlign="center" sx={{ opacity: 0.6 }}>
                <Button
                    variant="outlined"
                    color="error"
                    onClick={unsubscribeAll}
                    disabled={true}
                    sx={{
                        borderColor: "error.main",
                        color: "error.main",
                        "&:hover": {
                            backgroundColor: "error.main",
                            color: "white",
                            borderColor: "error.main",
                        },
                        opacity: 0.5,
                    }}
                >
                    Unsubscribe from all notifications
                </Button>
            </Box>

            <Snackbar
                open={showNotification}
                autoHideDuration={3000}
                onClose={handleCloseNotification}
                message="Notification settings updated successfully"
                sx={{
                    "& .MuiSnackbarContent-root": {
                        backgroundColor: "var(--primary-color)",
                    },
                }}
            />
        </Box>
    );
};

export default NotificationsTab;
